import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-6 pb-2" }
const _hoisted_2 = {
  key: 0,
  class: "text-base"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "flex flex-row flex-nowrap gap-3 items-center mb-2 text-m" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "flex flex-row flex-nowrap gap-3 items-center mb-2 text-m" }
const _hoisted_7 = ["innerHTML"]

import { ref, computed } from 'vue';
import { ComponentFields } from '@sitecore-jss/sitecore-jss/types/layout';
import { EYE_CLOSED, EYE_OPEN } from '@/constants/svg-images.const';

const lower = '[a-z]';
const upper = '[A-Z]';
const digit = '\\d';

const lengthRule = '{8,256}';

// ==========================================
// SECTION 4: Final Compilation
// ==========================================

export default /*@__PURE__*/_defineComponent({
  __name: 'PasswordInput',
  props: {
    fields: {},
    currentPasswordOnly: { type: Boolean },
    newPasswordLabel: {},
    confirmPasswordLabel: {},
    errors: {}
  },
  emits: ['valid', 'invalid'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = __props;

// ==========================================
// SECTION 1: Base Character Building Blocks
// ==========================================
const symbolList = [
  '\\s', ',', '.', '/', '<', '>', '?', ';', "'", ':', '"', 
  '\\[', '\\]', '\\\\', '|', '`', '~', '=', '\\-', '+', ')', 
  '(', '*', '&', '^', '%', '$', '#', '@', '!'
];
const symbol = `[${symbolList.join('')}]`;

const lowerRegex = new RegExp(lower);
const upperRegex = new RegExp(upper);
const digitRegex = new RegExp(digit);
const symbolRegex = new RegExp(symbol);

// ==========================================
// SECTION 2: Lookahead Combinations (3 out of 4)
// ==========================================
const combo1 = `(?=.*${lower})(?=.*${upper})(?=.*${digit})`;
const combo2 = `(?=.*${lower})(?=.*${upper})(?=.*${symbol})`;
const combo3 = `(?=.*${lower})(?=.*${digit})(?=.*${symbol})`;
const combo4 = `(?=.*${upper})(?=.*${digit})(?=.*${symbol})`;

// ==========================================
// SECTION 3: Global Restrictions (Characters & Length)
// ==========================================
const allowedAll = `[A-Za-z\\d${symbolList.join('')}]`;
const passwordRegex = new RegExp(
  `^(?:${combo1}|${combo2}|${combo3}|${combo4})${allowedAll}${lengthRule}$`
);

const password = ref('');
const password2 = ref('');

__expose({
  password,
  password2,
});

let is8Chars = ref<boolean | null>(null);
let hasCapital = ref<boolean | null>(null);
let hasNumber = ref<boolean | null>(null);
let hasLowercase = ref<boolean | null>(null);
let hasSymbol = ref<boolean | null>(null);
let inputType = ref<'password' | 'text'>('password');
let inputType2 = ref<'password' | 'text'>('password');
let isInvalidPassword2 = ref<boolean>(false);

function swapVisibility() {
  inputType.value = inputType.value === 'password' ? 'text' : 'password';
}

function swapVisibility2() {
  inputType2.value = inputType2.value === 'password' ? 'text' : 'password';
}

const threeRules = computed(() => {
  const validations = [hasCapital.value, hasNumber.value, hasLowercase.value, hasSymbol.value];
  if (validations.every((v) => v === null)) return null;
  return validations.filter((v) => v === true).length >= 3;
});

function handleChange(e: KeyboardEvent) {
  const val = (e.target as HTMLInputElement).value;
  is8Chars.value = val.length >= 8 ? true : is8Chars.value === false ? false : null;
  hasCapital.value = upperRegex.test(val) ? true : hasCapital.value == false ? false : null;
  hasNumber.value = digitRegex.test(val) ? true : hasNumber.value === false ? false : null;
  hasLowercase.value = lowerRegex.test(val) ? true : hasLowercase.value === false ? false : null;
  hasSymbol.value = symbolRegex.test(val)
    ? true
    : hasSymbol.value === false
      ? false
      : null;

  if (is8Chars.value && threeRules.value && password.value === password2.value) {
    emit('valid', password.value);
  } else {
    emit('invalid');
  }
}

function handleRetypeChange(e: KeyboardEvent) {
  const pass2value = (e.target as HTMLInputElement).value;
  isInvalidPassword2.value = password.value !== pass2value;
  if (password.value === pass2value) {
    emit('valid', password.value);
  } else {
    emit('invalid');
  }
}

function validate(e: FocusEvent) {
  const val = (e.target as HTMLInputElement).value;
  is8Chars.value = val.length >= 8;
  hasCapital.value = upperRegex.test(val);
  hasNumber.value = digitRegex.test(val);
  hasLowercase.value = lowerRegex.test(val);
  hasSymbol.value = symbolRegex.test(val);
}

function getValidationIcon(validated: boolean | null) {
  if (validated) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <g clip-path="url(#clip0_2254_724)">
        <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM11.51 6.19L7.68 10.89C7.53 11.06 7.33 11.16 7.12 11.16C6.91 11.16 6.71 11.07 6.56 10.9L4.53 8.68C4.23 8.39 4.23 7.87 4.53 7.55C4.66 7.4 4.85 7.31 5.05 7.3C5.27 7.3 5.48 7.39 5.63 7.55L7.08 9.12L10.33 5.12C10.46 4.95 10.68 4.84 10.88 4.84H11.08V4.87C11.22 4.89 11.34 4.96 11.44 5.06C11.76 5.34 11.8 5.86 11.52 6.19H11.51Z" fill="#128750"/>
      </g>
      <defs>
        <clipPath id="clip0_2254_724">
          <rect width="16" height="16" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
  }
  if (validated === false) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <g clip-path="url(#clip0_2254_746)">
        <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM10.79 9.73C11.08 10.02 11.08 10.5 10.79 10.79C10.64 10.94 10.45 11.01 10.26 11.01C10.07 11.01 9.88 10.94 9.73 10.79L8 9.06L6.27 10.79C6.12 10.94 5.93 11.01 5.74 11.01C5.55 11.01 5.36 10.94 5.21 10.79C4.92 10.5 4.92 10.02 5.21 9.73L6.94 8L5.21 6.27C4.92 5.98 4.92 5.5 5.21 5.21C5.5 4.92 5.98 4.92 6.27 5.21L8 6.94L9.73 5.21C10.02 4.92 10.5 4.92 10.79 5.21C11.08 5.5 11.08 5.98 10.79 6.27L9.06 8L10.79 9.73Z" fill="#D82900"/>
      </g>
      <defs>
        <clipPath id="clip0_2254_746">
          <rect width="16" height="16" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <g clip-path="url(#clip0_2254_17)">
        <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM11.51 6.19L7.68 10.89C7.53 11.06 7.33 11.16 7.12 11.16C6.91 11.16 6.71 11.07 6.56 10.9L4.53 8.68C4.23 8.39 4.23 7.87 4.53 7.55C4.66 7.4 4.85 7.31 5.05 7.3C5.27 7.3 5.48 7.39 5.63 7.55L7.08 9.12L10.33 5.12C10.46 4.95 10.68 4.84 10.88 4.84H11.08V4.87C11.22 4.89 11.34 4.96 11.44 5.06C11.76 5.34 11.8 5.86 11.52 6.19H11.51Z" fill="#D8E0E5"/>
      </g>
      <defs>
        <clipPath id="clip0_2254_17">
          <rect width="16" height="16" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_ctx.currentPasswordOnly)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, "Your new password must contain:"))
      : _createCommentVNode("", true),
    (!_ctx.currentPasswordOnly)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
          _createElementVNode("li", _hoisted_4, [
            _createElementVNode("span", {
              class: "flex align-middle",
              innerHTML: getValidationIcon(_unref(is8Chars))
            }, null, 8, _hoisted_5),
            _cache[2] || (_cache[2] = _createTextVNode(" At least 8 characters "))
          ]),
          _createElementVNode("li", _hoisted_6, [
            _createElementVNode("span", {
              innerHTML: getValidationIcon(threeRules.value)
            }, null, 8, _hoisted_7),
            _cache[3] || (_cache[3] = _createTextVNode(" Use 3 of the below: "))
          ]),
          _cache[4] || (_cache[4] = _createElementVNode("li", { class: "flex flex-row flex-nowrap gap-3 items-center mb-1 text-sm ml-7" }, "At least one uppercase letter", -1)),
          _cache[5] || (_cache[5] = _createElementVNode("li", { class: "flex flex-row flex-nowrap gap-3 items-center mb-1 text-sm ml-7" }, "At least one lowercase letter", -1)),
          _cache[6] || (_cache[6] = _createElementVNode("li", { class: "flex flex-row flex-nowrap gap-3 items-center mb-1 text-sm ml-7" }, "At least one number", -1)),
          _cache[7] || (_cache[7] = _createElementVNode("li", { class: "flex flex-row flex-nowrap gap-3 items-center mb-1 text-sm ml-7" }, "At least one symbol", -1))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_FormKit, {
      modelValue: password.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((password).value = $event)),
      name: "password",
      type: _unref(inputType),
      label: _ctx.newPasswordLabel ? _ctx.newPasswordLabel : 'Please enter your new password',
      inputClass: "h-[3rem] py-[0.75rem] md:h-[2.5rem] md:py-[0.5rem]",
      autocomplete: "new-password",
      suffixIcon: _unref(inputType) === 'password' ? _unref(EYE_OPEN) : _unref(EYE_CLOSED),
      suffixIconClass: "mx-1 [&>svg]:max-w-none [&>svg]:max-h-none",
      validation: [['required'], ['matches', _unref(passwordRegex)]],
      validationMessages: {
        required: 'This field cannot be blank',
        matches: 'Password does not meet requirements',
      },
      errors: _ctx.errors,
      onSuffixIconClick: swapVisibility,
      onKeyup: handleChange,
      onBlur: validate
    }, null, 8, ["modelValue", "type", "label", "suffixIcon", "validation", "errors"]),
    _createVNode(_component_FormKit, {
      modelValue: password2.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((password2).value = $event)),
      name: "confirmPassword",
      type: _unref(inputType2),
      label: _ctx.confirmPasswordLabel ? _ctx.confirmPasswordLabel : 'Please enter your new password',
      inputClass: "h-[3rem] py-[0.75rem] md:h-[2.5rem] md:py-[0.5rem]",
      autocomplete: "new-password",
      suffixIcon: _unref(inputType) === 'password' ? _unref(EYE_OPEN) : _unref(EYE_CLOSED),
      suffixIconClass: "mx-1 [&>svg]:max-w-none [&>svg]:max-h-none",
      validation: [['required'], ['confirm', 'password'], ['matches', _unref(passwordRegex)]],
      validationMessages: {
        required: 'This field cannot be blank',
        matches: 'Password does not meet requirements',
        confirm: 'Passwords must match',
      },
      onSuffixIconClick: swapVisibility2,
      onKeyup: handleRetypeChange
    }, null, 8, ["modelValue", "type", "label", "suffixIcon", "validation"])
  ]))
}
}

})